import type { ComputedRef, InjectionKey, Ref } from 'vue'

import type { GameViewMode, PresenterTransportKind } from './useGamePresentation'

// ── Shared shape ──────────────────────────────────────────
// Bundles the slice of useGamePresentation state that the GameFab (and
// any sibling overlay) needs to surface connection status, drive a
// reconnect, or hand off "take control." The page constructs this from
// its own useGamePresentation return value plus a page-supplied
// takeControl handler (the page knows about its store + persistence).

export interface GamePresenceContext {
  connected: ComputedRef<boolean> | Ref<boolean>
  // True while the peer transport is actively dialling. Distinct from
  // `connected` because the connect attempt can sit in retry/backoff for
  // a stretch before either side sees a state change.
  connecting: ComputedRef<boolean> | Ref<boolean>
  // Last transport error message, surfaced verbatim to the reconnect
  // modal so users can tell "wrong code" from "controller offline."
  lastError: Ref<null | string>
  // Controller's own pairing code (empty string when on presenter view).
  pairingCode: Ref<string>
  // Number of peer presenters currently connected to the controller.
  peerPresentersCount: ComputedRef<number> | Ref<number>
  // Code the presenter is trying to connect to (empty string when
  // viewing as controller).
  presenterTargetCode: Ref<string>
  presenterTransportKind: ComputedRef<PresenterTransportKind>
  // Trigger an immediate presenter-side reconnect, skipping the next
  // exponential-backoff wait. No-op on the controller view.
  retryConnection: () => void
  // Hand off the running game from the controller to this device. The
  // page persists current sync state to localStorage and strips the
  // presenter query params so this tab re-renders as the controller.
  // Undefined when take-control isn't applicable (e.g. broadcast popup
  // mode, or pages that haven't wired a handler).
  takeControl?: () => void
  viewMode: ComputedRef<GameViewMode>
}

export const GAME_PRESENCE_KEY: InjectionKey<GamePresenceContext> = Symbol('game:presence')

export function provideGamePresence(ctx: GamePresenceContext) {
  provide(GAME_PRESENCE_KEY, ctx)
}

export function useGamePresence(): GamePresenceContext | undefined {
  return inject(GAME_PRESENCE_KEY)
}
